import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { USER_ROLES, type UserRole, type Team } from '@/types/app';
import { useCreateUser } from './useUsers';

// Mirrors supabase/config.toml and the Edge Function's own validation.
const MIN_PASSWORD_LENGTH = 10;

/** A first password that satisfies the configured policy, for the admin to hand over. */
function suggestPassword(): string {
  const alphabet = 'abcdefghijkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '23456789';
  const all = alphabet + upper + digits;
  const bytes = crypto.getRandomValues(new Uint32Array(14));

  const chars = [
    upper[bytes[0]! % upper.length]!,
    alphabet[bytes[1]! % alphabet.length]!,
    digits[bytes[2]! % digits.length]!,
    ...Array.from(bytes.slice(3), (n) => all[n % all.length]!),
  ];
  return chars.join('');
}

interface CreateUserDialogProps {
  open: boolean;
  teams: Team[];
  onClose: () => void;
}

export function CreateUserDialog({ open, teams, onClose }: CreateUserDialogProps) {
  const { t } = useTranslation();
  const createUser = useCreateUser();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<UserRole>('sales');
  const [teamId, setTeamId] = useState('');
  const [password, setPassword] = useState(suggestPassword);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFullName('');
    setEmail('');
    setPhone('');
    setRole('sales');
    setTeamId('');
    setPassword(suggestPassword());
    setError(null);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      await createUser.mutateAsync({
        email,
        password,
        full_name: fullName,
        role,
        team_id: teamId || null,
        phone: phone.trim() || null,
      });
      reset();
      onClose();
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : '';
      setError(
        message.includes('email_taken') || message.includes('409')
          ? t('admin.users.emailTaken')
          : t('common.error'),
      );
    }
  };

  return (
    <Modal open={open} title={t('admin.users.create')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Input
          label={t('admin.users.fullName')}
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />

        <Input
          label={t('auth.email')}
          type="email"
          required
          dir="ltr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Input
          label={t('admin.users.phone')}
          type="tel"
          dir="ltr"
          placeholder="+201234567890"
          hint={t('admin.users.phoneHint')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />

        <Select
          label={t('admin.users.role')}
          required
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          options={USER_ROLES.map((r) => ({ value: r, label: t(`roles.${r}` as 'roles.sales') }))}
        />

        <Select
          label={t('admin.users.team')}
          placeholder={t('admin.users.noTeam')}
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          options={teams.map((team) => ({ value: team.id, label: team.name }))}
        />

        <Input
          label={t('admin.users.temporaryPassword')}
          required
          dir="ltr"
          minLength={MIN_PASSWORD_LENGTH}
          hint={t('admin.users.temporaryPasswordHint')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={createUser.isPending}>
            {t('admin.users.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
